import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EfFilters } from './EstadoFinancieroReport';
import { tituloColumna, formatMoney } from './EstadoFinancieroReport';
import { MovimientosDetalleDialog } from './MovimientosDetalleDialog';
import { ExportarXlsxButton } from './ExportarXlsxButton';
import { exportEvolucion } from './efExport';
import { getEvolucion, getEvolucionDetalle, type EvolucionAgrupar } from './api';

/**
 * Evolución — la misma matriz que el Estado Financiero, con el eje de filas
 * elegible: categoría, cuenta contable o SubÍtem del EF.
 *
 * Categoría y cuenta son las dos del modelo original, y sirven para mirar el
 * gasto por su nombre operativo ("Logistica", "Sueldos"). SubÍtem del EF es
 * nuestro agregado: la taxonomía contable está completa y sin nombres viejos,
 * así que esas filas cruzan exacto contra el Estado Financiero.
 *
 * Respeta el mismo período, granularidad y moneda que el resto del tablero, y
 * aplica las mismas exclusiones — si difirieran, las solapas contarían
 * universos distintos y no habría forma de saber cuál creer.
 */
export function EvolucionPanel({ state }: { state: EfFilters }) {
  const { periodo } = state;
  const { config: activeCountry } = useActiveCountry();
  const [agrupar, setAgrupar] = useState<EvolucionAgrupar>('categoria');
  const [celda, setCelda] = useState<{ label: string; mes: string } | null>(null);

  const q = useQuery({
    queryKey: ['estado-financiero-evolucion', activeCountry.code, periodo, agrupar],
    queryFn: () => getEvolucion(periodo, agrupar),
    staleTime: 60_000,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 items-center gap-1">
        <span className="mr-1 text-sm text-muted-foreground">Agrupar por:</span>
        {(
          [
            { id: 'categoria', label: 'Categoría' },
            { id: 'cuenta', label: 'Cuenta contable' },
            // Nuestro agregado sobre el modelo: la taxonomía del EF no
            // arrastra nombres viejos y cruza directo contra el estado.
            { id: 'subitem', label: 'SubÍtem EF' },
          ] as const
        ).map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setAgrupar(o.id)}
            aria-pressed={agrupar === o.id}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              agrupar === o.id
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {o.label}
          </button>
        ))}
        {q.data && q.data.rows.length > 0 && (
          <div className="ml-auto">
            <ExportarXlsxButton
              onExport={() => exportEvolucion(q.data, periodo.desde, periodo.hasta)}
            />
          </div>
        )}
      </div>

      {q.isError ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden />
            No se pudo calcular la evolución.
          </div>
        </div>
      ) : q.isPending ? (
        <div className="min-h-0 flex-1 space-y-2 rounded-2xl border bg-card p-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : q.data.rows.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card text-sm text-muted-foreground">
          Sin movimientos en el período seleccionado.
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-2xl border bg-card">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 border-b bg-card px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {agrupar === 'categoria'
                    ? 'Categoría'
                    : agrupar === 'cuenta'
                      ? 'Cuenta contable'
                      : 'SubÍtem EF'}
                </th>
                {q.data.meses.map((m) => (
                  <th
                    key={m}
                    className="whitespace-nowrap border-b bg-card px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {tituloColumna(m, q.data.granularidad)}
                  </th>
                ))}
                <th className="whitespace-nowrap border-b bg-card px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total real
                </th>
                {q.data.proyeccion.meses.map((m) => (
                  <th
                    key={m}
                    className="whitespace-nowrap border-b bg-sky-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-sky-800"
                    title="Proyectado con el promedio recortado de los últimos meses"
                  >
                    {`Proy. ${m}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.label} className="border-b hover:bg-primary/5">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 max-w-[20rem] truncate border-b bg-card px-4 py-2.5 text-left font-normal"
                    title={r.label}
                  >
                    {r.label}
                  </th>
                  {q.data.meses.map((m) => (
                    <Celda
                      key={m}
                      valor={r.porMes[m]}
                      moneda={q.data.moneda}
                      onClick={() => setCelda({ label: r.label, mes: m })}
                    />
                  ))}
                  <Celda valor={r.total} moneda={q.data.moneda} emphasis />
                  {q.data.proyeccion.meses.map((m) => (
                    <Celda
                      key={m}
                      valor={q.data.proyeccion.porLabel[r.label]?.[m]}
                      moneda={q.data.moneda}
                      proyectada
                    />
                  ))}
                </tr>
              ))}
              <tr className="border-b bg-muted/40 font-semibold">
                <th
                  scope="row"
                  className="sticky left-0 z-10 border-b bg-muted/60 px-4 py-2.5 text-left"
                >
                  Total
                </th>
                {q.data.meses.map((m) => (
                  <Celda key={m} valor={q.data.totalPorMes[m]} moneda={q.data.moneda} emphasis />
                ))}
                <Celda valor={q.data.total} moneda={q.data.moneda} emphasis />
                {q.data.proyeccion.meses.map((m) => (
                  <Celda
                    key={m}
                    valor={Object.values(q.data.proyeccion.porLabel).reduce(
                      (acc, porMes) => acc + (porMes[m] ?? 0),
                      0,
                    )}
                    moneda={q.data.moneda}
                    emphasis
                    proyectada
                  />
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {q.data && q.data.proyeccion.meses.length > 0 && (
        <p className="shrink-0 text-xs text-muted-foreground">
          Las columnas <span className="italic">Proy.</span> son una estimación:
          promedio recortado de los últimos meses de cada fila, que descarta los
          extremos para que un mes atípico no arrastre el resultado. Se
          configura en Configuración → Parámetros.
        </p>
      )}

      <EvolucionDetalleDialog
        celda={celda}
        agrupar={agrupar}
        vista={periodo.vista}
        casa={periodo.casa}
        granularidad={q.data?.granularidad ?? 'mensual'}
        onClose={() => setCelda(null)}
      />
    </div>
  );
}

/** Movimientos detrás de una celda (fila × período) de la matriz. */
function EvolucionDetalleDialog({
  celda,
  agrupar,
  vista,
  casa,
  granularidad,
  onClose,
}: {
  celda: { label: string; mes: string } | null;
  agrupar: EvolucionAgrupar;
  vista: EfFilters['periodo']['vista'];
  casa: EfFilters['periodo']['casa'];
  granularidad: 'mensual' | 'diario';
  onClose: () => void;
}) {
  const { config: activeCountry } = useActiveCountry();
  const q = useQuery({
    queryKey: [
      'evolucion-detalle',
      activeCountry.code,
      celda?.label,
      celda?.mes,
      agrupar,
      vista,
      casa,
    ],
    queryFn: () =>
      getEvolucionDetalle({ mes: celda!.mes, label: celda!.label, agrupar, vista, casa }),
    enabled: !!celda,
    staleTime: 60_000,
  });

  return (
    <MovimientosDetalleDialog
      abierto={!!celda}
      titulo={
        celda ? `${celda.label} · ${tituloColumna(celda.mes, granularidad)}` : ''
      }
      rows={q.data?.rows ?? []}
      moneda={q.data?.moneda ?? ''}
      cargando={q.isPending}
      error={q.isError}
      onClose={onClose}
    />
  );
}

function Celda({
  valor,
  moneda,
  emphasis,
  proyectada,
  onClick,
}: {
  valor: number | undefined;
  moneda: string;
  emphasis?: boolean;
  proyectada?: boolean;
  onClick?: () => void;
}) {
  const vacio = valor === undefined || valor === 0;
  return (
    <td
      className={cn(
        'whitespace-nowrap border-b px-4 py-2.5 text-right tabular-nums',
        emphasis && 'font-semibold',
        // Proyectado: fondo tenue + itálica, para que nunca se lea como real.
        proyectada && 'bg-sky-50/60 italic',
        onClick && !vacio && 'cursor-pointer hover:underline',
      )}
      onClick={vacio ? undefined : onClick}
      title={onClick && !vacio ? 'Ver movimientos' : undefined}
    >
      {vacio ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className={valor! < 0 ? 'text-red-600' : 'text-emerald-700'}>
          {formatMoney(valor!, moneda)}
        </span>
      )}
    </td>
  );
}
